import { PATHS } from '../paths';

/**
 * A row the client can open, named the way the client thinks about it.
 *
 * Deliberately **not** keyed on the assistant's `EntityRefType`: that union is
 * a model-facing choice set (`@orn/global/assistant/inlineMarkup.js`) whose
 * five members happen to coincide with this one today, while this one names
 * every record kind a surface can open. Keying on it would force a surface
 * that has nothing to do with the assistant to either widen the model's
 * grammar or grow a second copy of this mapping.
 */
export type EntityTarget =
  | { type: 'merchant'; id: string }
  | { type: 'financialAccount'; id: string }
  | { type: 'transaction'; id: string }
  | { type: 'bill'; id: string }
  | { type: 'recurringTransaction'; id: string };

/**
 * Where opening a row goes. Merchants and accounts have routes; a transaction,
 * bill, or recurring stream is only reachable through a slideout its opener has
 * to mount, so those name the slideout to mount instead of a path.
 */
export type EntityDestination =
  | { kind: 'navigate'; path: string }
  | { kind: 'transactionSlideout'; transactionId: string }
  | { kind: 'billSlideout'; billId: string }
  | { kind: 'recurringTransactionSlideout'; recurringTransactionId: string };

/**
 * Resolve a row to the thing that opens it.
 *
 * Total by construction — every target has somewhere to go, so a caller never
 * has to decide what an unopenable row looks like. A new member of
 * {@link EntityTarget} does not compile until it picks a destination.
 */
export function entityDestination(target: EntityTarget): EntityDestination {
  switch (target.type) {
    case 'merchant':
      return { kind: 'navigate', path: PATHS.MERCHANT(target.id) };
    case 'financialAccount':
      return { kind: 'navigate', path: PATHS.ACCOUNT(target.id) };
    case 'transaction':
      return { kind: 'transactionSlideout', transactionId: target.id };
    case 'bill':
      return { kind: 'billSlideout', billId: target.id };
    case 'recurringTransaction':
      return { kind: 'recurringTransactionSlideout', recurringTransactionId: target.id };
    default: {
      const _exhaustive: never = target;
      return _exhaustive;
    }
  }
}

/**
 * The path a destination can be linked to, or `null` when it has no URL.
 *
 * A chip renders an anchor when this returns a path, so cmd-click and
 * copy-link-address behave the way they do everywhere else. A slideout has no
 * address to offer, so its chip is a button instead.
 */
export function entityHref(target: EntityTarget): string | null {
  const destination = entityDestination(target);
  return destination.kind === 'navigate' ? destination.path : null;
}
